import React, { useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import EmptyState from 'koba/components/EmptyState';
import { SkeletonPost } from 'koba/components/SkeletonPost/SkeletonPost';

import { useCommunityContext } from 'components/CommunityContext';
import { NUMBER_OF_REACTIONS } from 'utils/constants';
import { POST_QUERY } from 'schema/Post/queries';
import {
  PostQuery,
  PostQueryVariables,
} from '__generated__/graphql/legacy/graphql';
import { goToPostListPage, goToSpaces } from 'utils/routerHelpers';
import { replaceMentions } from 'utils/mentions';
import CommunityHeader from 'components/CommunityHeader/CommunityHeader';

import PostContainer from 'components/PostContainer/PostContainer';

import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb';
import { getIconNameByStoredValue } from 'helpers/spaceHelpers';
import { getLayout } from 'components/Layouts/MainLayout';
import styles from '../index.module.scss';

const cx = classNames.bind(styles);

interface PostPageProps {
  postListState: {
    useCacheFirst: boolean;
  };
}

const PostPage = ({ postListState }: PostPageProps) => {
  useEffect(() => {
    // In the future we may want to update this state properly using setState but for now :this-is-fine:
    // Did this way to avoid any unnecessary app wide re-renders
    // eslint-disable-next-line no-param-reassign
    postListState.useCacheFirst = true;
  }, []);

  const onPostChange = () => {
    // When something changes on the post page that affects whether or not a post
    // appears in the PostList, we shouldn't use cache-first when we navigate back to it
    // eslint-disable-next-line no-param-reassign
    postListState.useCacheFirst = false;
  };
  const { t } = useTranslation();
  const { community } = useCommunityContext();
  const lastCommentRef = useRef<HTMLDivElement>(null);
  const communityWrapperRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const postId = router?.query?.postId as string;

  const { loading, data } = useQuery<PostQuery, PostQueryVariables>(
    POST_QUERY,
    {
      variables: {
        communityId: community.id,
        postId,
        numberOfReactions: NUMBER_OF_REACTIONS,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const post = data?.community?.post;

  const content = replaceMentions(post?.content, post?.mentionedUsers);
  const communitySpaceData = data?.community?.post?.communitySpace;

  return (
    <>
      <CommunityHeader
        pageTitle={t('pages-post_title', 'Post {{title}}', {
          title: post?.title,
        })}
      />

      <section className={cx('community-main__post')}>
        {communitySpaceData && (
          <BreadCrumb
            buttonTitle={communitySpaceData.name || undefined}
            currentPageType={t('Post', 'Post')}
            goToFunction={() => {
              goToSpaces(router, communitySpaceData.id, community.id);
            }}
            iconName={getIconNameByStoredValue(
              communitySpaceData.iconName || ''
            )}
          />
        )}
        {loading && <SkeletonPost />}
        {!loading &&
          (post ? (
            <PostContainer
              additionalClassName="post__detail"
              author={post.user}
              communityWrapperRef={communityWrapperRef}
              content={content}
              goToPostListPage={goToPostListPage(router, community.id)}
              key={post.id}
              lastCommentRef={lastCommentRef}
              mentionedUsers={post?.mentionedUsers}
              post={post}
              t={t}
              isPostPage
              onPostChange={onPostChange}
            />
          ) : (
            <EmptyState
              className="empty-state__full-width"
              title={t('Post not found')}
            />
          ))}
      </section>
    </>
  );
};

PostPage.getLayout = getLayout;

export default PostPage;
